import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Thermometer, TrendingUp, Bell, BellOff } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const BBTTrackerCard = () => {
  const { token } = useAuth();
  const [temp, setTemp] = useState("");
  const [unit, setUnit] = useState("F");
  const [readings, setReadings] = useState([]);
  const [shiftDate, setShiftDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/wellness/ttc/bbt?days=45`, { headers, withCredentials: true });
      setReadings(res.data.readings || []);
      setShiftDate(res.data.temp_shift_date);
    } catch {
      /* silent */
    }
  };

  const loadReminder = async () => {
    try {
      const res = await axios.get(`${API}/wellness/ttc/bbt/reminder`, { headers, withCredentials: true });
      setReminderEnabled(!!res.data.enabled);
    } catch { /* silent */ }
  };

  useEffect(() => { refresh(); loadReminder(); /* eslint-disable-next-line */ }, [token]);

  const toggleReminder = async () => {
    const next = !reminderEnabled;
    try {
      await axios.post(`${API}/wellness/ttc/bbt/reminder`,
        { enabled: next, hour_utc: 7 },
        { headers, withCredentials: true }
      );
      setReminderEnabled(next);
      toast.success(next ? "Morning reminder enabled ⏰" : "Morning reminder disabled");
    } catch {
      toast.error("Could not update reminder");
    }
  };

  const logTemp = async () => {
    if (!temp) return toast.error("Enter a temperature");
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/ttc/bbt`, {
        temperature: parseFloat(temp),
        unit,
      }, { headers, withCredentials: true });
      toast.success("BBT logged");
      setTemp("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save BBT");
    } finally {
      setSaving(false);
    }
  };

  const chartData = readings.map(r => ({ date: r.date.slice(5), temp: r.temperature_f }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.22 }}
      data-testid="bbt-tracker-card"
    >
      <Card className="glass border-rose-400/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-rose-300" />
              Basal Body Temperature
            </CardTitle>
            <button
              onClick={toggleReminder}
              data-testid="bbt-reminder-toggle"
              aria-label={reminderEnabled ? "Disable morning reminder" : "Enable morning reminder"}
              className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-all ${
                reminderEnabled
                  ? 'bg-rose-500/20 border-rose-400/40 text-rose-100'
                  : 'bg-mystic-800/50 border-white/10 text-mystic-300 hover:text-white'
              }`}
            >
              {reminderEnabled ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
              {reminderEnabled ? 'Reminder on' : 'Enable reminder'}
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-mystic-200 text-xs">Today's temp</Label>
              <input
                type="number"
                step="0.01"
                value={temp}
                onChange={(e) => setTemp(e.target.value)}
                placeholder={unit === "F" ? "97.5" : "36.4"}
                className="mt-1 w-full bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
                data-testid="bbt-temp-input"
              />
            </div>
            <div>
              <Label className="text-mystic-200 text-xs">Unit</Label>
              <div className="mt-1 flex gap-1">
                {["F", "C"].map(u => (
                  <button
                    key={u}
                    onClick={() => setUnit(u)}
                    data-testid={`bbt-unit-${u.toLowerCase()}-btn`}
                    className={`px-3 py-2 rounded-lg text-sm ${unit === u ? "bg-rose-500/30 border border-rose-400 text-white" : "bg-mystic-800/50 border border-white/10 text-mystic-200"}`}
                  >°{u}</button>
                ))}
              </div>
            </div>
            <div className="flex items-end">
              <Button
                onClick={logTemp}
                disabled={saving}
                data-testid="log-bbt-btn"
                className="bg-rose-500 hover:bg-rose-400 text-white rounded-full"
              >
                {saving ? "..." : "Log"}
              </Button>
            </div>
          </div>

          {chartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                  <XAxis dataKey="date" stroke="#c4a0ff" fontSize={10} />
                  <YAxis stroke="#c4a0ff" fontSize={10} domain={[96, 99.5]} />
                  <Tooltip contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8, color: "#fff" }} />
                  <Line type="monotone" dataKey="temp" stroke="#f472b6" strokeWidth={2} dot={{ r: 3 }} />
                  {shiftDate && (
                    <ReferenceLine
                      x={shiftDate.slice(5)}
                      stroke="#fbbf24"
                      strokeDasharray="4 2"
                      label={{ value: "Ovulation", position: "top", fill: "#fbbf24", fontSize: 10 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              {shiftDate && (
                <div className="flex items-center gap-2 text-sm text-amber-300" data-testid="bbt-shift-detected">
                  <TrendingUp className="w-4 h-4" />
                  Temperature shift detected on {shiftDate} — ovulation likely occurred just before.
                </div>
              )}
            </>
          ) : (
            <p className="text-mystic-200/60 text-sm text-center py-4">
              Log temperatures for 6+ consecutive mornings to see your pattern and ovulation shift.
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default BBTTrackerCard;
